// React
import React, { Fragment, useMemo } from 'react'

// Components
import BDatePicker, { HostStyles } from 'store/blocks/CartPicker/Button'

// Libraries
import { faCalendar as falCalendar, faChevronRight as falChevronRight } from '@fortawesome/pro-light-svg-icons'

// Hooks
import { useOrm, shallowEqual } from 'shared/utils/useOrm'
import { useOpeningHours } from 'shared/utils/useOpeningHours'
import { useSettings } from 'shared/utils/useSettings'
import { useTranslation } from 'shared/utils/useTranslation'
import { useDatePicker } from 'store/hooks/useDatePicker'
import { useSelector } from 'react-redux'
import { useBrandingColor } from 'shared/utils/useBrandingColor'

// Shared
import { Format as FormatUtils } from 'back_office/v2/utils/utils'
import { isLocationInputEnabled, isDaySelectable, isTimeSelectable } from 'ui/components/Picker/utils'

const getLabelKey = (pricingEnabled, availabilityEnabled) => {
  if (pricingEnabled && availabilityEnabled) {
    return 'user.store.view_prices_and_availability'
  }

  if (pricingEnabled && !availabilityEnabled) {
    return 'user.store.view_prices'
  }

  if (!pricingEnabled && availabilityEnabled) {
    return 'user.store.view_availability'
  }
}

export const CartPickerButton = () => {
  const openingHours = useOpeningHours()

  const cartId = useSelector((state) => state.client.cartId)

  const cart = useOrm('carts', (carts) => {
    return carts.getById(cartId || window.booqableOptions.cart?.id)
  })
  const locations = useOrm('locations', (locations) => {
    return {
      count: locations.size(),
      start: locations.getById(cart?.start_location_id ?? window.booqableOptions.cart?.startLocationId),
      stop: locations.getById(cart?.stop_location_id ?? window.booqableOptions.cart?.stopLocationId)
    }
  }, shallowEqual)

  const useTimes = useSettings('store.use_times')
  const useAMOrPM = useSettings('dates.use_am_pm')
  const isPricingEnabled = useSettings('pricing.enabled')
  const isAvailabilityEnabled = useSettings('store.use_availability')

  const brandingColor = useBrandingColor()

  const { open } = useDatePicker()

  const dates = {
    start: cart?.starts_at ?? window.booqableOptions.cart?.from,
    stop: cart?.stops_at ?? window.booqableOptions.cart?.till
  }

  const isDelivery = cart?.data?.fulfillment_type === 'delivery'

  const isStartValid = useMemo(
    () =>
      isDaySelectable({
        type: 'start',
        openingHours,
        date: dates.start,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }) &&
      isTimeSelectable({
        type: 'start',
        openingHours,
        date: dates.start,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }),
    [dates.start, dates.stop, openingHours]
  )

  const isStopValid = useMemo(
    () =>
      isDaySelectable({
        type: 'stop',
        openingHours,
        date: dates.stop,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }) &&
      isTimeSelectable({
        type: 'stop',
        openingHours,
        date: dates.stop,
        values: {
          starts_at: dates.start,
          stops_at: dates.stop
        }
      }),
    [dates.start, dates.stop, openingHours]
  )

  const { t } = useTranslation('user')

  const hasLocations = locations.count > 1 && (isLocationInputEnabled('start') || isLocationInputEnabled('stop'))
  const hasData = hasLocations ?
    !!dates.start && !!dates.stop && !!locations.start && !!locations.stop :
    !!dates.start && !!dates.stop

  let timeFormat = ''

  if (useTimes) {
    timeFormat = useAMOrPM ? ' hh:mm A' : ' HH:mm'
  }

  const labelKey = getLabelKey(isPricingEnabled, isAvailabilityEnabled)

  if (!cart) return null

  if (!hasData) {
    return (
      <Fragment>
        <HostStyles />
        <BDatePicker data-tid="Cart picker button">
          <BDatePicker.Cleanstate brandingColor={brandingColor} onClick={open}>
            <BDatePicker.CalendarIcon icon={falCalendar} />
            <BDatePicker.CleanstateLabels>
              <BDatePicker.CleanstateTitle>{t('user.store.select_a_period')}</BDatePicker.CleanstateTitle>
              <BDatePicker.CleanstateLabel>{labelKey ? t(labelKey) : '...'}</BDatePicker.CleanstateLabel>
            </BDatePicker.CleanstateLabels>
            <BDatePicker.ChevronIcon icon={falChevronRight} />
          </BDatePicker.Cleanstate>
        </BDatePicker>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <HostStyles />
      <BDatePicker data-tid="Cart picker button">
        <BDatePicker.Wrapper onClick={open} hasLocations={hasLocations}>
          <BDatePicker.SectionGroup>
            <BDatePicker.Section>
              <BDatePicker.Label>{isDelivery ? t('user.store.timeslot_from') : t('user.store.start_date')}</BDatePicker.Label>
              <BDatePicker.Value isPlaceholder={!dates.start} isValid={isStartValid}>
                {dates.start && (
                  <Fragment>
                    {FormatUtils.date(dates.start, 'MMM D[,] YYYY')}
                    {useTimes && FormatUtils.date(dates.start, timeFormat)}
                  </Fragment>
                )}
                {!dates.start && t('user.store.pick_date')}
              </BDatePicker.Value>
            </BDatePicker.Section>
            <BDatePicker.Section>
              <BDatePicker.Label>{isDelivery ? t('user.store.timeslot_until') : t('user.store.stop_date')}</BDatePicker.Label>
              <BDatePicker.Value isPlaceholder={!dates.stop} isValid={isStopValid}>
                {dates.stop && (
                  <Fragment>
                    {FormatUtils.date(dates.stop, 'MMM D[,] YYYY')}
                    {useTimes && FormatUtils.date(dates.stop, timeFormat)}
                  </Fragment>
                )}
                {!dates.stop && t('user.store.pick_date')}
              </BDatePicker.Value>
            </BDatePicker.Section>
          </BDatePicker.SectionGroup>
          {hasLocations && !isDelivery && (
            <BDatePicker.SectionGroup>
              <BDatePicker.Section>
                <BDatePicker.Label>{t('user.store.start_location')}</BDatePicker.Label>
                <BDatePicker.Value isPlaceholder={!locations.start} isValid>
                  {locations.start?.name ?? t('user.store.start_location')}
                </BDatePicker.Value>
              </BDatePicker.Section>
              <BDatePicker.Section>
                <BDatePicker.Label>{t('user.store.stop_location')}</BDatePicker.Label>
                <BDatePicker.Value isPlaceholder={!locations.stop} isValid>
                  {locations.stop?.name ?? t('user.store.stop_location')}
                </BDatePicker.Value>
              </BDatePicker.Section>
            </BDatePicker.SectionGroup>
          )}
        </BDatePicker.Wrapper>
      </BDatePicker>
    </Fragment>
  )
}
